import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  FaCalendarAlt,
  FaFilter,
  FaSyncAlt,
  FaRupeeSign,
  FaCoins,
  FaPercentage,
  FaBalanceScale,
  FaStar,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";

const emptyStats = {
  revenue: 0,
  driverEarnings: 0,
  totalCommission: 0,
  commissionPaid: 0,
  commissionPending: 0,
  totalGst: 0,
  gstPaid: 0,
  gstPending: 0,
  totalDue: 0,
  duePaid: 0,
  duePending: 0,
  averageCustomerRating: 0,
  averageDriverRating: 0,
};

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const formatMoney = (value) => currencyFormatter.format(Number(value) || 0);

const formatRating = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "0.0";
  return numeric.toFixed(1);
};

const cardBaseClass =
  "rounded-xl p-4 shadow-md text-white transition-transform duration-150";

const summaryCards = [
  {
    key: "revenue",
    label: "Revenue",
    icon: FaRupeeSign,
    gradient: "bg-gradient-to-r from-sky-600 to-blue-700",
  },
  {
    key: "driverEarnings",
    label: "Driver Earnings",
    icon: FaCoins,
    gradient: "bg-gradient-to-r from-emerald-600 to-green-700",
  },
  {
    key: "totalCommission",
    label: "Total Commission",
    icon: FaPercentage,
    gradient: "bg-gradient-to-r from-indigo-600 to-violet-700",
    subItems: [
      { label: "Commission Paid", key: "commissionPaid" },
      { label: "Commission Pending", key: "commissionPending" },
    ],
  },
  {
    key: "totalGst",
    label: "Total GST",
    icon: FaRupeeSign,
    gradient: "bg-gradient-to-r from-orange-500 to-amber-600",
    subItems: [
      { label: "GST Paid", key: "gstPaid" },
      { label: "GST Pending", key: "gstPending" },
    ],
  },
  {
    key: "totalDue",
    label: "Total Due",
    icon: FaBalanceScale,
    gradient: "bg-gradient-to-r from-rose-500 to-red-600",
    subItems: [
      { label: "Due Paid", key: "duePaid" },
      { label: "Due Pending", key: "duePending" },
    ],
  },
  {
    key: "averageRating",
    label: "Average Rating",
    icon: FaStar,
    gradient: "bg-gradient-to-r from-cyan-600 to-teal-700",
    subItems: [
      { label: "Customer", key: "averageCustomerRating", isRating: true },
      { label: "Driver", key: "averageDriverRating", isRating: true },
    ],
  },
];

const SummaryCard = ({ cfg, stats, loading }) => {
  const Icon = cfg.icon;
  const hasSubCards = Array.isArray(cfg.subItems) && cfg.subItems.length > 0;

  if (hasSubCards) {
    const moneySubItems = (cfg.subItems || []).filter((item) => !item.isRating);
    const subTotal = moneySubItems.reduce(
      (sum, item) => sum + (Number(stats[item.key]) || 0),
      0
    );
    const showInlineTotal = moneySubItems.length > 0;

    return (
      <div className={`${cardBaseClass} ${cfg.gradient} text-left w-full`}>
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs uppercase tracking-wider text-white/95 truncate">
              {cfg.label}
            </span>
            {showInlineTotal && (
              <span className="text-sm font-semibold text-white whitespace-nowrap">
                {loading ? (
                  <span className="inline-block h-4 w-14 rounded bg-white/30 animate-pulse" />
                ) : (
                  <span className="text-white/90">({formatMoney(subTotal)})</span>
                )}
              </span>
            )}
          </div>
          <span className="h-6 w-6 rounded-full bg-white/20 border border-white/30 inline-flex items-center justify-center flex-shrink-0">
            <Icon className="text-xs text-white" />
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          {cfg.subItems.map((item) => (
            <div
              key={item.key}
              className="rounded-lg bg-white/14 border border-white/20 px-2 py-2 text-center"
            >
              <p className="text-[11px] leading-4 text-white/90 truncate">
                {item.label}
              </p>
              <p className="text-2xl font-bold leading-tight mt-1 text-white">
                {loading ? (
                  <span className="inline-block h-7 w-16 rounded bg-white/30 animate-pulse" />
                ) : item.isRating ? (
                  formatRating(stats[item.key])
                ) : (
                  formatMoney(stats[item.key])
                )}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`${cardBaseClass} ${cfg.gradient} text-left w-full`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider opacity-90 truncate pr-2">
          {cfg.label}
        </span>
        <Icon className="text-lg opacity-80 flex-shrink-0" />
      </div>
      <div className="text-3xl font-bold leading-tight">
        {loading ? (
          <span className="inline-block h-8 w-24 rounded bg-white/30 animate-pulse" />
        ) : (
          formatMoney(stats[cfg.key])
        )}
      </div>
    </div>
  );
};

const DashboardRevenueMetrics = () => {
  const [stats, setStats] = useState(emptyStats);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ startDate: "", endDate: "" });
  const [filtersApplied, setFiltersApplied] = useState(false);

  const fetchMetrics = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (params.startDate && params.endDate) {
        queryParams.set("startDate", params.startDate);
        queryParams.set("endDate", params.endDate);
      }

      const qs = queryParams.toString();
      const url = qs
        ? `${API.ADMIN.RIDES_STATUS_BREAKDOWN}?${qs}`
        : API.ADMIN.RIDES_STATUS_BREAKDOWN;

      const response = await axiosInstance.get(url);
      if (response.data?.success) {
        setStats({ ...emptyStats, ...(response.data.data || {}) });
        setFiltersApplied(Boolean(params.startDate && params.endDate));
      } else {
        toast.error("Failed to load revenue metrics");
      }
    } catch (error) {
      console.error("Revenue metrics fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const handleApplyFilters = () => {
    if (!filters.startDate || !filters.endDate) {
      toast.warning("Please select both start date and end date");
      return;
    }
    if (filters.startDate > filters.endDate) {
      toast.warning("Start date cannot be greater than end date");
      return;
    }
    fetchMetrics({
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
  };

  const handleClearFilters = () => {
    setFilters({ startDate: "", endDate: "" });
    fetchMetrics();
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          Revenue Metrics
        </h3>
        <p className="text-gray-600 text-sm">
          {filtersApplied
            ? "Filtered by selected created date range (same as All Rides breakdown)"
            : "Overall totals across all rides (same as All Rides breakdown)"}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, startDate: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date
          </label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, endDate: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={handleApplyFilters}
            disabled={loading}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-60"
          >
            <FaFilter className="inline mr-2" />
            Filter Apply
          </button>
        </div>
        <div className="flex items-end">
          <button
            onClick={handleClearFilters}
            disabled={loading}
            className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-60"
          >
            <FaSyncAlt className="inline mr-2" />
            Clear
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-600">
          <FaCalendarAlt className="inline mr-2 animate-pulse" />
          Loading revenue metrics...
        </div>
      ) : (
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">
            Revenue and Rating Breakdown
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {summaryCards.map((cfg) => (
              <SummaryCard
                key={cfg.key}
                cfg={cfg}
                stats={stats}
                loading={loading}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardRevenueMetrics;
